import { useEffect, useRef } from 'react'

import { type Query, useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { salesforcePushStatusQueryKeys } from '@/hooks/salesforce/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { StatusResponse } from '@/types/Status'

interface UseSyncPursuitsToSalesforceStatusArgs {
  orgId: UUID | ''
  onSuccess?: () => void
}

export const fetchSyncPursuitsToSalesforceStatus = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<StatusResponse> => {
  if (!orgId) throw new Error('Organization ID is required')
  const url = `${API_URL}/api/v1/jobs/push-salesforce/key/${orgId}`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
  return (await res.json()) as StatusResponse
}

const refetchInterval = (data: Query<StatusResponse>) => {
  if (
    data.state.data?.status === 'pending' ||
    data.state.data?.status === 'running'
  ) {
    return 5 * 1000
  }
  return 5 * 60 * 1000
}

export const useSyncPursuitsToSalesforceStatus = ({
  orgId,
  onSuccess,
}: UseSyncPursuitsToSalesforceStatusArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const wasRunningRef = useRef<boolean>(false)

  const query = useQuery({
    queryKey: salesforcePushStatusQueryKeys.organization({ orgId }),
    queryFn: () => fetchSyncPursuitsToSalesforceStatus(orgId, accessToken),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    refetchInterval,
  })

  useEffect(() => {
    // TODO: for some reason this gets fired multiple times,
    // even if the status string hasn't changed;
    // does this happen for the other integration status hooks?
    if (!query.data) {
      return
    }
    if (query.data.status === 'pending' || query.data.status === 'running') {
      wasRunningRef.current = true
      return
    }
    if (query.data.status === 'succeeded' && wasRunningRef.current) {
      onSuccess?.()
    }
  }, [query.data?.status, onSuccess])

  return query
}
